import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, CircularProgress, ListItem, Tooltip, useTheme } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import { FORMS_ROUTING_PARAMS, FormsPaths } from 'app/configs/routesConfig/forms';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import LazyWrapper from 'app/shared/components/common/LazyWrapper';
import Tag from 'app/shared/components/common/Tag';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { FormViewerNavigationType } from 'constants/forms';
import { PAGE_CODES } from 'constants/pageCodes';
import { TagColor } from 'constants/ui';
import { useAsyncStateSafeStrictMode } from 'hooks/useAsyncStateSafeStrictMode';

import { getIsFromInternalCompany, getMasterPermissionsToPage } from 'store/auth/selectors';
import { deleteFormAction } from 'store/forms/actions';
import { selectFormNavigationType } from 'store/forms/selectors';
import { openConfirmationDialogAction } from 'store/global/actions';

import { FormsTestIds } from '../../Forms.testIds';

import { dateContainerStyles, getStyles } from './FormListItem.styles';

const FormListItem = ({ form, selectedFormId, isHideTag = false, onClickFormItem = _.noop }) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const dispatch = useDispatch();
  const { formId: currentFormId, area: currentArea, historyEntityId } = useParams();
  const { push } = useHistory();

  const { formId, lastModified, name, state, companyId, formType } = form;

  const navigationType = useSelector(selectFormNavigationType);
  const isNavigationNotRequired = navigationType === FormViewerNavigationType.STATE;

  const isSelected = isNavigationNotRequired ? selectedFormId === formId : currentFormId === formId;

  const [isDeleting, setIsDeleting] = useAsyncStateSafeStrictMode(false);

  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.FORMS));
  const isUserFromInternalCompany = useSelector(getIsFromInternalCompany);
  const isDocumentRelatedToInternalCompany = checkIsInternalCompany({ companyId });
  const isOverrideEnabled = useFeatureToggle(FeatureFlag.EnableFormLocalOverrides);
  const isOverrideActive = isOverrideEnabled && !isUserFromInternalCompany;

  const isDeleteIconHidden =
    !isManageAllowed || (!isUserFromInternalCompany && isDocumentRelatedToInternalCompany);

  const viewFormPath = useMemo(
    () =>
      FormsPaths.viewForm.generatePath({
        [FORMS_ROUTING_PARAMS.formId]: formId,
        [FORMS_ROUTING_PARAMS.area]: state,
        [FORMS_ROUTING_PARAMS.formType]: formType,
        [FORMS_ROUTING_PARAMS.selectedPage]: 1,
        [FORMS_ROUTING_PARAMS.historyEntityId]: historyEntityId,
      }),
    [formId, state, formType, historyEntityId],
  );

  const onDeleteForm = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm form deleting',
        description: 'Are you sure you want to delete form?',
        onAccept: () => {
          setIsDeleting(true);

          dispatch(deleteFormAction({ formId }))
            .then(() => {
              if (isNavigationNotRequired) {
                onClickFormItem(null);

                return;
              }

              if (currentFormId === formId) {
                FormsPaths.formsInArea.navigateWith(push, {
                  [FORMS_ROUTING_PARAMS.area]: currentArea,
                });
              }
            })
            .finally(() => setIsDeleting(false));
        },
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [
      currentArea,
      currentFormId,
      dispatch,
      formId,
      isNavigationNotRequired,
      onClickFormItem,
      push,
      setIsDeleting,
    ],
  );

  const onSelectItem = useCallback(() => {
    onClickFormItem(form);
  }, [form, onClickFormItem]);

  return (
    <LazyWrapper once={false}>
      {viewportRef => (
        <ListItem
          component={isNavigationNotRequired ? undefined : NavLink}
          data-name={`forms_list-item_form-name-${name}`}
          data-selected={isSelected}
          data-testid={FormsTestIds.getFormsListItemFormId(formId)}
          ref={viewportRef}
          selected={isSelected}
          sx={isSelected ? classes.itemRootSelected : classes.itemRoot}
          to={viewFormPath}
          onClick={onSelectItem}
        >
          <div className="flex w-full">
            <Tooltip title={name}>
              <Box className="font-600 flex-1" sx={classes.name}>
                {name}
              </Box>
            </Tooltip>
            {!isHideTag && !isOverrideActive && (
              <Tag
                backgroundColor={
                  isDocumentRelatedToInternalCompany ? TagColor.BLUE : TagColor.YELLOW
                }
                style={classes.tag}
                text={isDocumentRelatedToInternalCompany ? 'Global' : 'Local'}
              />
            )}
          </div>
          <Box
            className="flex w-full items-center"
            sx={isOverrideEnabled ? dateContainerStyles : undefined}
          >
            <div className="text-11 italic w-full items-center">
              {dayjs(lastModified).format(COMMON_DATETIME_FORMAT_FOR_TREE_ITEM)}
            </div>
            {isDeleting ? (
              <CircularProgress color="secondary" size={25} />
            ) : (
              <>
                {!isDeleteIconHidden && (
                  <ButtonWithTooltip
                    color="inherit"
                    data-name="delete-form-button"
                    data-testid={FormsTestIds.FORM_LIST_ITEM_DELETE_BUTTON}
                    icon={DeleteIcon}
                    iconButtonClassName="p-1 delete-btn"
                    iconClassName="tool__disable-interaction"
                    iconSize={IconSize[20]}
                    title="Delete"
                    onClick={onDeleteForm}
                  />
                )}
              </>
            )}
          </Box>
        </ListItem>
      )}
    </LazyWrapper>
  );
};

FormListItem.propTypes = {
  form: PropTypes.object.isRequired,
  selectedFormId: PropTypes.string,
  isHideTag: PropTypes.bool,
  onClickFormItem: PropTypes.func,
};

export default memo(FormListItem);
